import { useCallback, useEffect, useRef, useState, type TouchEvent } from "react";
import { Link } from "react-router-dom";
import type { MediaItem } from "@/types/media";

interface HeroCarouselProps {
  items: MediaItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const count = items.length;

  const go = useCallback(
    (next: number) => {
      setIndex(((next % count) + count) % count);
    },
    [count]
  );

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % count);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [count]);

  const onTouchStart = (e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      go(index + (delta < 0 ? 1 : -1));
    }
    touchStartX.current = null;
  };

  return (
    <section
      className="relative w-full h-[460px] md:h-[600px] overflow-hidden"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100 z-10" : "opacity-0 z-0"
          }`}
        >
          <img
            src={item.backdrop || item.poster}
            alt={`${item.title} 剧照`}
            title={`${item.title} - 光棍影院`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/55 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 to-transparent" />

          <div className="relative z-10 h-full w-full px-4 md:px-8">
            <div className="max-w-2xl h-full flex flex-col justify-center">
              <div className="flex items-center gap-2 mb-3 animate-fade-up">
                <span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-primary-500 text-background-50">
                  {item.type === "movie" ? "电影" : "电视剧"}
                </span>
                <span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-accent-500 text-foreground-950">
                  编辑精选
                </span>
                <span className="flex items-center gap-1 text-sm font-semibold text-accent-400">
                  <i className="ri-star-fill"></i>
                  {item.rating.toFixed(1)}
                </span>
              </div>
              <h2 className="text-3xl md:text-5xl font-bold text-background-50 font-heading tracking-tight">
                {item.title}
              </h2>
              <p className="mt-2 text-sm md:text-base text-background-200">
                {item.genre.join(" · ")} | {item.region} | {item.year} | {item.duration}
              </p>
              <p className="mt-4 text-sm md:text-base text-background-300 leading-relaxed line-clamp-3 max-w-xl">
                {item.synopsis}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to={`/detail/${item.id}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-foreground-950 text-sm font-semibold hover:bg-accent-400 transition-colors whitespace-nowrap"
                >
                  <i className="ri-play-circle-line text-lg"></i>
                  查看详情
                </Link>
                <Link
                  to={`/detail/${item.id}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50/15 backdrop-blur-sm text-background-50 text-sm font-semibold hover:bg-background-50/25 transition-colors whitespace-nowrap"
                >
                  <i className="ri-information-line text-lg"></i>
                  剧情介绍
                </Link>
              </div>
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(index - 1)}
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-foreground-950/40 hover:bg-foreground-950/70 backdrop-blur-sm items-center justify-center text-background-50 transition-colors"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(index + 1)}
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-foreground-950/40 hover:bg-foreground-950/70 backdrop-blur-sm items-center justify-center text-background-50 transition-colors"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 张`}
            onClick={() => go(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? "w-7 bg-accent-500" : "w-2.5 bg-background-50/50"
            }`}
          />
        ))}
      </div>
    </section>
  );
}